import { intToBigInt } from '@stacks/common';
import { PostConditionMode, PostConditionPrincipalId, PostConditionType, } from './constants';
import { StacksWireType, addressToString, parseAssetString, parsePrincipalString, serializePostConditionWire, } from './wire';
const MAX_U64 = BigInt('18446744073709551615');
export function parsePostConditionAmount(amount) {
    const value = intToBigInt(amount);
    if (value < 0n || value > MAX_U64) {
        throw new RangeError(`Post-condition amount must be between 0 and ${MAX_U64} (u64 max), received: ${value}`);
    }
    return value;
}
var PostConditionCodeWireType;
(function (PostConditionCodeWireType) {
    PostConditionCodeWireType[PostConditionCodeWireType["eq"] = 1] = "eq";
    PostConditionCodeWireType[PostConditionCodeWireType["gt"] = 2] = "gt";
    PostConditionCodeWireType[PostConditionCodeWireType["lt"] = 4] = "lt";
    PostConditionCodeWireType[PostConditionCodeWireType["gte"] = 3] = "gte";
    PostConditionCodeWireType[PostConditionCodeWireType["lte"] = 5] = "lte";
    PostConditionCodeWireType[PostConditionCodeWireType["sent"] = 16] = "sent";
    PostConditionCodeWireType[PostConditionCodeWireType["not-sent"] = 17] = "not-sent";
    PostConditionCodeWireType[PostConditionCodeWireType["maybe-sent"] = 18] = "maybe-sent";
})(PostConditionCodeWireType || (PostConditionCodeWireType = {}));
var PoxConditionCodeWireType;
(function (PoxConditionCodeWireType) {
    PoxConditionCodeWireType[PoxConditionCodeWireType["will-not-perform"] = 48] = "will-not-perform";
    PoxConditionCodeWireType[PoxConditionCodeWireType["may-perform"] = 49] = "may-perform";
    PoxConditionCodeWireType[PoxConditionCodeWireType["will-perform"] = 50] = "will-perform";
})(PoxConditionCodeWireType || (PoxConditionCodeWireType = {}));
export function postConditionToWire(postcondition) {
    switch (postcondition.type) {
        case 'stx-postcondition':
            return {
                type: StacksWireType.PostCondition,
                conditionType: PostConditionType.STX,
                principal: postcondition.address === 'origin'
                    ? { type: StacksWireType.Principal, prefix: PostConditionPrincipalId.Origin }
                    : parsePrincipalString(postcondition.address),
                conditionCode: conditionTypeToByte(postcondition.condition),
                amount: parsePostConditionAmount(postcondition.amount),
            };
        case 'ft-postcondition':
            return {
                type: StacksWireType.PostCondition,
                conditionType: PostConditionType.Fungible,
                principal: postcondition.address === 'origin'
                    ? { type: StacksWireType.Principal, prefix: PostConditionPrincipalId.Origin }
                    : parsePrincipalString(postcondition.address),
                conditionCode: conditionTypeToByte(postcondition.condition),
                amount: parsePostConditionAmount(postcondition.amount),
                asset: parseAssetString(postcondition.asset),
            };
        case 'nft-postcondition':
            return {
                type: StacksWireType.PostCondition,
                conditionType: PostConditionType.NonFungible,
                principal: postcondition.address === 'origin'
                    ? { type: StacksWireType.Principal, prefix: PostConditionPrincipalId.Origin }
                    : parsePrincipalString(postcondition.address),
                conditionCode: conditionTypeToByte(postcondition.condition),
                asset: parseAssetString(postcondition.asset),
                assetName: postcondition.assetId,
            };
        case 'staking-postcondition':
            return {
                type: StacksWireType.PostCondition,
                conditionType: PostConditionType.Staking,
                principal: postcondition.address === 'origin'
                    ? { type: StacksWireType.Principal, prefix: PostConditionPrincipalId.Origin }
                    : parsePrincipalString(postcondition.address),
                conditionCode: conditionTypeToByte(postcondition.condition),
                amount: parsePostConditionAmount(postcondition.amount),
            };
        case 'pox-postcondition':
            return {
                type: StacksWireType.PostCondition,
                conditionType: PostConditionType.PoX,
                principal: postcondition.address === 'origin'
                    ? { type: StacksWireType.Principal, prefix: PostConditionPrincipalId.Origin }
                    : parsePrincipalString(postcondition.address),
                conditionCode: poxConditionTypeToByte(postcondition.condition),
            };
        default:
            throw new Error('Invalid post condition type');
    }
}
export function wireToPostCondition(wire) {
    switch (wire.conditionType) {
        case PostConditionType.STX:
            return {
                type: 'stx-postcondition',
                address: principalWireToString(wire.principal),
                condition: conditionByteToType(wire.conditionCode),
                amount: wire.amount.toString(),
            };
        case PostConditionType.Fungible:
            return {
                type: 'ft-postcondition',
                address: principalWireToString(wire.principal),
                condition: conditionByteToType(wire.conditionCode),
                amount: wire.amount.toString(),
                asset: assetWireToString(wire.asset),
            };
        case PostConditionType.NonFungible:
            return {
                type: 'nft-postcondition',
                address: principalWireToString(wire.principal),
                condition: conditionByteToType(wire.conditionCode),
                asset: assetWireToString(wire.asset),
                assetId: wire.assetName,
            };
        case PostConditionType.Staking:
            return {
                type: 'staking-postcondition',
                address: principalWireToString(wire.principal),
                condition: conditionByteToType(wire.conditionCode),
                amount: wire.amount.toString(),
            };
        case PostConditionType.PoX:
            return {
                type: 'pox-postcondition',
                address: principalWireToString(wire.principal),
                condition: poxConditionByteToType(wire.conditionCode),
            };
        default: {
            const _exhaustiveCheck = wire;
            throw new Error(`Invalid post condition type: ${_exhaustiveCheck}`);
        }
    }
}
export function conditionTypeToByte(condition) {
    return PostConditionCodeWireType[condition];
}
export function conditionByteToType(wireType) {
    return PostConditionCodeWireType[wireType];
}
export function poxConditionTypeToByte(condition) {
    return PoxConditionCodeWireType[condition];
}
export function poxConditionByteToType(wireType) {
    return PoxConditionCodeWireType[wireType];
}
export function postConditionToHex(postcondition) {
    const wire = postConditionToWire(postcondition);
    return serializePostConditionWire(wire);
}
export function postConditionModeFrom(mode) {
    if (typeof mode === 'number')
        return mode;
    if (mode === 'allow')
        return PostConditionMode.Allow;
    if (mode === 'deny')
        return PostConditionMode.Deny;
    if (mode === 'originator')
        return PostConditionMode.Originator;
    throw new Error(`Invalid post condition mode: ${mode}`);
}
function assetWireToString(asset) {
    const address = addressToString(asset.address);
    const contractId = `${address}.${asset.contractName.content}`;
    return `${contractId}::${asset.assetName.content}`;
}
function principalWireToString(principal) {
    switch (principal.prefix) {
        case PostConditionPrincipalId.Origin:
            return 'origin';
        case PostConditionPrincipalId.Standard:
            return addressToString(principal.address);
        case PostConditionPrincipalId.Contract:
            const address = addressToString(principal.address);
            return `${address}.${principal.contractName.content}`;
        default:
            const _exhaustiveCheck = principal;
            throw new Error(`Invalid principal type: ${_exhaustiveCheck}`);
    }
}
//# sourceMappingURL=postcondition.js.map